if (!customElements.get('shoppable-video')) {
  customElements.define(
    'shoppable-video',
    class ShoppableVideo extends HTMLElement {
      constructor() {
        super();
        this.activeIndex = 0;
        this.dragged = false;
      }

      connectedCallback() {
        this.popup = this.querySelector('.psvs-popup');
        this.slides = this.querySelectorAll('.psvs-popup-slide');
        this.slider = this.querySelector('.psvs-slider');
        if (!this.popup || !this.slides.length) return;

        this.onKeydown = this.onKeydown.bind(this);
        this.onWheel = this.onWheel.bind(this);

        this.querySelectorAll('.psvs-media-wrap').forEach((card) => {
          const open = () => this.openPopup(Number(card.closest('.psvs-card').dataset.index));
          card.addEventListener('click', () => {
            if (!this.dragged) open();
          });
          card.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              open();
            }
          });
        });

        this.querySelector('.psvs-close').addEventListener('click', () => this.closePopup());
        this.querySelector('.psvs-next').addEventListener('click', () => this.nextSlide());
        this.querySelector('.psvs-prev').addEventListener('click', () => this.prevSlide());
        this.popup.addEventListener('click', (event) => {
          if (event.target === this.popup) this.closePopup();
        });

        document.addEventListener('keydown', this.onKeydown);
        this.addEventListener('wheel', this.onWheel, { passive: false, capture: true });
        this.initDrag();
        this.initLazyVideos();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeydown);
        document.body.style.overflow = '';
        if (this.videoObserver) this.videoObserver.disconnect();
        if (this.posterObserver) this.posterObserver.disconnect();
      }

      /* Card videos load and play only while on screen; they pause when scrolled away.
         With Save-Data or reduced motion, cards keep their poster image. */
      initLazyVideos() {
        this.visibleVideos = new Set();
        const cardVideos = this.querySelectorAll('.psvs-card-video');
        if (!cardVideos.length) return;

        this.initLazyPosters(cardVideos);

        const saveData = navigator.connection && navigator.connection.saveData;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.autoplayCards = !saveData && !reducedMotion;
        if (!this.autoplayCards || !('IntersectionObserver' in window)) return;

        this.videoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach(({ target: video, isIntersecting }) => {
              if (isIntersecting) {
                this.visibleVideos.add(video);
                this.playCardVideo(video);
              } else {
                this.visibleVideos.delete(video);
                video.pause();
              }
            });
          },
          { rootMargin: '200px 0px', threshold: 0.01 }
        );

        cardVideos.forEach((video) => this.videoObserver.observe(video));
      }

      /* Posters (data-poster) load only when the card is near the screen */
      initLazyPosters(cardVideos) {
        const setPoster = (video) => {
          if (video.dataset.poster && !video.getAttribute('poster')) video.poster = video.dataset.poster;
        };

        if (!('IntersectionObserver' in window)) {
          cardVideos.forEach(setPoster);
          return;
        }

        this.posterObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach(({ target, isIntersecting }) => {
              if (!isIntersecting) return;
              setPoster(target);
              observer.unobserve(target);
            });
          },
          { rootMargin: '400px 0px' }
        );

        cardVideos.forEach((video) => this.posterObserver.observe(video));
      }

      playCardVideo(video) {
        if (this.popup.classList.contains('active')) return;
        if (!video.getAttribute('src') && video.dataset.src) {
          video.src = video.dataset.src;
        }
        if (!video.getAttribute('src')) return;
        video.muted = true;
        video.play().catch(() => {});
      }

      /* Keep vertical mouse-wheel scrolling on the page, even when the cursor is
         positioned over the horizontally scrollable video slider. */
      onWheel(event) {
        if (this.popup.classList.contains('active')) return;

        const isVerticalScroll = Math.abs(event.deltaY) > Math.abs(event.deltaX);
        if (!isVerticalScroll || event.deltaY === 0) return;

        event.preventDefault();
        event.stopPropagation();

        let scrollAmount = event.deltaY;
        if (event.deltaMode === 1) scrollAmount *= 16;
        if (event.deltaMode === 2) scrollAmount *= window.innerHeight;

        (document.scrollingElement || document.documentElement).scrollTop += scrollAmount;
      }

      initDrag() {
        const slider = this.slider;
        if (!slider) return;

        let isDown = false;
        let startX;
        let scrollLeft;

        slider.style.cursor = 'grab';

        slider.addEventListener('mousedown', (event) => {
          if (window.innerWidth < 750) return;
          isDown = true;
          this.dragged = false;
          slider.style.cursor = 'grabbing';
          startX = event.pageX - slider.offsetLeft;
          scrollLeft = slider.scrollLeft;
        });

        const stop = () => {
          isDown = false;
          slider.style.cursor = 'grab';
        };
        slider.addEventListener('mouseleave', stop);
        slider.addEventListener('mouseup', stop);

        slider.addEventListener('mousemove', (event) => {
          if (!isDown) return;
          event.preventDefault();
          const x = event.pageX - slider.offsetLeft;
          if (Math.abs(x - startX) > 5) this.dragged = true;
          slider.scrollLeft = scrollLeft - (x - startX) * 1.5;
        });
      }

      stopAllVideos() {
        this.querySelectorAll('video').forEach((video) => {
          video.pause();
          if (video.getAttribute('src') || video.querySelector('source')) video.currentTime = 0;
        });
      }

      /* Resume only the cards that are on screen (not all of them) */
      playCardVideos() {
        if (!this.autoplayCards || !this.visibleVideos) return;
        this.visibleVideos.forEach((video) => this.playCardVideo(video));
      }

      updatePopup() {
        const last = this.slides.length - 1;

        this.slides.forEach((slide, index) => {
          slide.classList.remove('active', 'prev', 'next');

          if (index === this.activeIndex) {
            slide.classList.add('active');
            const video = slide.querySelector('video');
            if (video) {
              video.muted = false;
              video.volume = 1;
              video.play().catch(() => {});
            }
          }

          if (index === this.activeIndex - 1 || (this.activeIndex === 0 && index === last)) {
            slide.classList.add('prev');
          }

          if (index === this.activeIndex + 1 || (this.activeIndex === last && index === 0)) {
            slide.classList.add('next');
          }
        });
      }

      openPopup(index) {
        this.activeIndex = index;
        this.popup.classList.add('active');
        document.body.style.overflow = 'hidden';
        this.stopAllVideos();
        this.updatePopup();
        this.querySelector('.psvs-close').focus();
      }

      closePopup() {
        this.popup.classList.remove('active');
        document.body.style.overflow = '';
        this.stopAllVideos();
        this.playCardVideos();
      }

      nextSlide() {
        this.stopAllVideos();
        this.activeIndex = (this.activeIndex + 1) % this.slides.length;
        this.updatePopup();
      }

      prevSlide() {
        this.stopAllVideos();
        this.activeIndex = (this.activeIndex - 1 + this.slides.length) % this.slides.length;
        this.updatePopup();
      }

      onKeydown(event) {
        if (!this.popup.classList.contains('active')) return;
        if (event.key === 'Escape') this.closePopup();
        if (event.key === 'ArrowRight') this.nextSlide();
        if (event.key === 'ArrowLeft') this.prevSlide();
      }
    }
  );
}
